// ============================================
// Ambiguity Protocol — HLX-CAP.02 D#5
//
// When an extracted biomarker name matches multiple
// canonical candidates, this module applies concrete
// context resolution rules to disambiguate — or
// defaults to "unidentified" if no rule resolves.
//
// Rules (evaluated in order):
//   1. Section header match (200px vertical distance)
//   2. Sibling biomarker match (2+ from same clinical group)
//   3. Reference range match (10% tolerance)
//   4. Unit match (unambiguous mapping)
//
// If no rule resolves OR rules disagree → unidentified.
// Clinical neighbors NEVER resolved by context alone.
// ============================================

// ── Types ──

export interface SourceLocation {
  page: number;
  x: number;
  y: number;
}

export interface SectionHeader {
  text: string;
  page: number;
  y: number;
}

export interface SiblingBiomarker {
  canonicalId: string;
  page: number;
}

export interface AmbiguousCandidate {
  canonicalId: string;
  clinicalGroup: ClinicalGroup;
}

export interface DocumentContext {
  sectionHeaders: SectionHeader[];
  siblingBiomarkers: SiblingBiomarker[];
}

export interface AmbiguityInput {
  rawName: string;
  candidates: AmbiguousCandidate[];
  sourceLocation: SourceLocation;
  extractedRange?: { low: number; high: number };
  extractedUnit?: string;
  documentContext: DocumentContext;
}

export type ResolutionMethod =
  | 'section_header'
  | 'sibling_biomarker'
  | 'reference_range'
  | 'unit_match';

export interface AmbiguityResult {
  resolved: boolean;
  canonicalId: string | null;
  method: ResolutionMethod | null;
  resolutionNotes: string;
  processingFlags: string[];
}

// ── Clinical Groups ──

export type ClinicalGroup =
  | 'thyroid'
  | 'hormonal'
  | 'lipid'
  | 'metabolic'
  | 'inflammatory'
  | 'nutritional'
  | 'hepatic'
  | 'renal'
  | 'hematologic'
  | 'autoimmune'
  | 'tumor_marker'
  | 'heavy_metal'
  | 'longevity'
  | 'cardiac'
  | 'reproductive';

/**
 * Maps canonical biomarker IDs to their clinical group.
 * Used for sibling biomarker matching (Rule 2).
 */
export const BIOMARKER_CLINICAL_GROUPS: Record<string, ClinicalGroup> = {
  // Thyroid
  tsh: 'thyroid',
  free_t3: 'thyroid',
  free_t4: 'thyroid',
  total_t3: 'thyroid',
  total_t4: 'thyroid',
  reverse_t3: 'thyroid',
  t3_uptake: 'thyroid',
  free_t4_index: 'thyroid',
  tpo_antibodies: 'thyroid',
  thyroglobulin_antibodies: 'thyroid',

  // Hormonal
  testosterone_total_male: 'hormonal',
  testosterone_total_female: 'hormonal',
  free_testosterone_male: 'hormonal',
  free_testosterone_female: 'hormonal',
  estradiol_male: 'hormonal',
  estradiol_female: 'hormonal',
  progesterone_female: 'hormonal',
  progesterone_male: 'hormonal',
  fsh_female: 'hormonal',
  fsh_male: 'hormonal',
  lh_female: 'hormonal',
  lh_male: 'hormonal',
  dheas_female: 'hormonal',
  dheas_male: 'hormonal',
  shbg_female: 'hormonal',
  shbg_male: 'hormonal',
  prolactin_male: 'hormonal',
  prolactin_female: 'hormonal',
  cortisol_am: 'hormonal',
  dht: 'hormonal',

  // Lipid
  total_cholesterol: 'lipid',
  ldl: 'lipid',
  hdl: 'lipid',
  triglycerides: 'lipid',
  vldl: 'lipid',
  tc_hdl_ratio: 'lipid',
  chol_hdl_ratio: 'lipid',
  non_hdl_cholesterol: 'lipid',
  apolipoprotein_b: 'lipid',
  lpa: 'lipid',
  ldl_particle_number: 'lipid',
  ldl_small: 'lipid',
  oxidized_ldl: 'lipid',

  // Metabolic
  glucose: 'metabolic',
  fasting_glucose: 'metabolic',
  fasting_insulin: 'metabolic',
  hba1c: 'metabolic',
  homa_ir: 'metabolic',
  uric_acid: 'metabolic',

  // Inflammatory
  hscrp: 'inflammatory',
  homocysteine: 'inflammatory',
  esr: 'inflammatory',
  fibrinogen: 'inflammatory',
  il6: 'inflammatory',
  tnf_alpha: 'inflammatory',

  // Nutritional
  vitamin_d: 'nutritional',
  b12: 'nutritional',
  folate: 'nutritional',
  ferritin: 'nutritional',
  iron: 'nutritional',
  magnesium_rbc: 'nutritional',
  zinc: 'nutritional',
  tibc: 'nutritional',
  iron_saturation: 'nutritional',
  selenium: 'nutritional',

  // Hepatic
  alt: 'hepatic',
  ast: 'hepatic',
  ggt: 'hepatic',
  alp: 'hepatic',
  bilirubin: 'hepatic',
  albumin: 'hepatic',

  // Renal
  bun: 'renal',
  creatinine: 'renal',
  egfr: 'renal',
  sodium: 'renal',
  potassium: 'renal',
  chloride: 'renal',
  co2: 'renal',
  calcium: 'renal',

  // Hematologic
  wbc: 'hematologic',
  rbc: 'hematologic',
  hemoglobin: 'hematologic',
  hematocrit: 'hematologic',
  platelets: 'hematologic',
  mcv: 'hematologic',
  mch: 'hematologic',
  mchc: 'hematologic',
  rdw: 'hematologic',
  mpv: 'hematologic',
  neutrophils: 'hematologic',
  lymphocytes: 'hematologic',
  monocytes: 'hematologic',
  eosinophils: 'hematologic',
  basophils: 'hematologic',

  // Tumor markers
  psa: 'tumor_marker',
  ca125: 'tumor_marker',
  cea: 'tumor_marker',
  afp: 'tumor_marker',

  // Heavy metals
  mercury: 'heavy_metal',
  lead: 'heavy_metal',
  arsenic: 'heavy_metal',
  cadmium: 'heavy_metal',

  // Longevity
  igf1: 'longevity',
  gh: 'longevity',

  // Reproductive
  amh: 'reproductive',

  // Cardiac
  bnp: 'cardiac',
  nt_probnp: 'cardiac',
};

// ── Clinical Neighbor Pairs ──
// Ambiguity between clinical neighbors is NEVER resolved by context alone.
// Requires exact alias match or explicit alias table entry.

const CLINICAL_NEIGHBOR_PAIRS: [string, string][] = [
  ['estradiol_female', 'estrone'],
  ['estradiol_male', 'estrone'],
  ['free_t3', 'free_t4'],
  ['total_t3', 'total_t4'],
  ['free_t3', 'total_t3'],
  ['free_t4', 'total_t4'],
  ['alt', 'ast'],
];

function areClinicalNeighbors(idA: string, idB: string): boolean {
  return CLINICAL_NEIGHBOR_PAIRS.some(
    ([a, b]) =>
      (a === idA && b === idB) || (a === idB && b === idA),
  );
}

function candidatesAreClinicalNeighbors(candidates: AmbiguousCandidate[]): boolean {
  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      if (areClinicalNeighbors(candidates[i].canonicalId, candidates[j].canonicalId)) {
        return true;
      }
    }
  }
  return false;
}

// ── Section Header Keywords ──
// Maps header keywords to clinical groups they indicate.

const HEADER_GROUP_KEYWORDS: Record<string, ClinicalGroup[]> = {
  thyroid: ['thyroid'],
  hormone: ['hormonal'],
  testosterone: ['hormonal'],
  estrogen: ['hormonal'],
  reproductive: ['hormonal', 'reproductive'],
  lipid: ['lipid'],
  cholesterol: ['lipid'],
  metabolic: ['metabolic'],
  glucose: ['metabolic'],
  inflammatory: ['inflammatory'],
  inflammation: ['inflammatory'],
  nutritional: ['nutritional'],
  vitamin: ['nutritional'],
  mineral: ['nutritional'],
  hepatic: ['hepatic'],
  liver: ['hepatic'],
  renal: ['renal'],
  kidney: ['renal'],
  cbc: ['hematologic'],
  hematology: ['hematologic'],
  'complete blood': ['hematologic'],
  tumor: ['tumor_marker'],
  cancer: ['tumor_marker'],
  heavy_metal: ['heavy_metal'],
  metal: ['heavy_metal'],
  toxic: ['heavy_metal'],
};

// ── Known Reference Ranges for Disambiguation ──
// Maps canonical IDs to their expected standard reference ranges + unit.
// Used for Rule 3: reference range matching within 10% tolerance.

interface KnownRange {
  low: number;
  high: number;
  unit: string;
}

const KNOWN_REFERENCE_RANGES: Record<string, KnownRange[]> = {
  // Thyroid
  tsh: [{ low: 0.4, high: 4.5, unit: 'mIU/L' }],
  free_t3: [{ low: 2.0, high: 4.4, unit: 'pg/mL' }],
  free_t4: [{ low: 0.8, high: 1.8, unit: 'ng/dL' }],
  total_t3: [{ low: 80, high: 200, unit: 'ng/dL' }],
  total_t4: [{ low: 4.5, high: 12.0, unit: 'µg/dL' }],
  reverse_t3: [{ low: 9.2, high: 24.1, unit: 'ng/dL' }],

  // Hormonal — Male
  testosterone_total_male: [{ low: 264, high: 916, unit: 'ng/dL' }],
  free_testosterone_male: [{ low: 8.7, high: 25.1, unit: 'pg/mL' }],

  // Hormonal — Female
  estradiol_female: [{ low: 12.5, high: 166, unit: 'pg/mL' }],
  progesterone_female: [{ low: 1.8, high: 24, unit: 'ng/mL' }],

  // Metabolic
  glucose: [{ low: 65, high: 99, unit: 'mg/dL' }],
  fasting_glucose: [{ low: 65, high: 99, unit: 'mg/dL' }],
  fasting_insulin: [{ low: 2.6, high: 24.9, unit: 'µIU/mL' }],

  // Lipid
  total_cholesterol: [{ low: 125, high: 200, unit: 'mg/dL' }],
  ldl: [{ low: 0, high: 130, unit: 'mg/dL' }],
  hdl: [{ low: 40, high: 100, unit: 'mg/dL' }],
  triglycerides: [{ low: 0, high: 150, unit: 'mg/dL' }],

  // Hepatic
  alt: [{ low: 7, high: 56, unit: 'U/L' }],
  ast: [{ low: 10, high: 40, unit: 'U/L' }],
  ggt: [{ low: 0, high: 65, unit: 'U/L' }],
  alp: [{ low: 44, high: 147, unit: 'U/L' }],

  // Renal
  bun: [{ low: 6, high: 20, unit: 'mg/dL' }],
  creatinine: [{ low: 0.6, high: 1.2, unit: 'mg/dL' }],

  // Inflammatory
  hscrp: [{ low: 0, high: 3.0, unit: 'mg/L' }],
  homocysteine: [{ low: 0, high: 15, unit: 'µmol/L' }],
  esr: [{ low: 0, high: 20, unit: 'mm/hr' }],

  // Nutritional
  vitamin_d: [{ low: 30, high: 100, unit: 'ng/mL' }],
  b12: [{ low: 200, high: 900, unit: 'pg/mL' }],
  ferritin: [
    { low: 12, high: 150, unit: 'ng/mL' },
    { low: 12, high: 300, unit: 'ng/mL' },
  ],
  iron: [{ low: 60, high: 170, unit: 'µg/dL' }],

  // Hematologic
  wbc: [{ low: 4.5, high: 11.0, unit: 'x10³/µL' }],
  rbc: [{ low: 4.0, high: 5.5, unit: 'x10⁶/µL' }],
  hemoglobin: [{ low: 12.0, high: 17.5, unit: 'g/dL' }],
  hematocrit: [{ low: 36, high: 52, unit: '%' }],
  platelets: [{ low: 150, high: 400, unit: 'x10³/µL' }],

  // Longevity
  igf1: [{ low: 53, high: 331, unit: 'ng/mL' }],

  // Hormonal — Both
  cortisol_am: [{ low: 6.2, high: 19.4, unit: 'µg/dL' }],
  dheas_female: [{ low: 35, high: 430, unit: 'µg/dL' }],
  dheas_male: [{ low: 80, high: 560, unit: 'µg/dL' }],
};

// ── Unit → Candidate Mapping ──
// Maps units that unambiguously identify a clinical domain/candidate.

const UNIT_TO_CANDIDATES: Record<string, string[]> = {
  'mIU/L': ['tsh'],
  'µIU/mL': ['tsh', 'fasting_insulin'],
  'pg/mL': ['free_t3', 'free_testosterone_male', 'free_testosterone_female', 'estradiol_female', 'estradiol_male', 'b12'],
  'ng/dL': ['total_t3', 'reverse_t3', 'free_t4', 'testosterone_total_male', 'testosterone_total_female'],
  'µg/dL': ['total_t4', 'cortisol_am', 'dheas_female', 'dheas_male', 'iron'],
  'ng/mL': ['vitamin_d', 'ferritin', 'progesterone_female', 'progesterone_male', 'igf1', 'psa'],
  'mg/dL': ['glucose', 'fasting_glucose', 'total_cholesterol', 'ldl', 'hdl', 'triglycerides', 'bun', 'creatinine', 'uric_acid', 'magnesium_rbc', 'calcium'],
  'mg/L': ['hscrp'],
  'µmol/L': ['homocysteine'],
  'mm/hr': ['esr'],
  'U/L': ['alt', 'ast', 'ggt', 'alp'],
  'nmol/L': ['shbg_female', 'shbg_male'],
  'IU/mL': ['tpo_antibodies'],
  'g/dL': ['hemoglobin', 'albumin', 'total_protein'],
  '%': ['hba1c', 'hematocrit'],
  'x10³/µL': ['wbc', 'platelets'],
  'x10⁶/µL': ['rbc'],
};

// ── Resolution Rules ──

const HEADER_MAX_VERTICAL_DISTANCE_PX = 200;

/**
 * Rule 1: Section Header Match
 * If the ambiguous name appears under a section header that maps to
 * one of the candidate's clinical groups, and the header is within
 * 200px vertical distance above the biomarker, resolve to that candidate.
 */
function resolveByHeader(input: AmbiguityInput): AmbiguousCandidate | null {
  const { sourceLocation, documentContext, candidates } = input;

  // Find headers on the same page, above the biomarker, within 200px
  const nearbyHeaders = documentContext.sectionHeaders.filter(
    (h) =>
      h.page === sourceLocation.page &&
      h.y <= sourceLocation.y &&
      sourceLocation.y - h.y <= HEADER_MAX_VERTICAL_DISTANCE_PX,
  );

  if (nearbyHeaders.length === 0) return null;

  // Sort by closest (largest y = closest above)
  nearbyHeaders.sort((a, b) => b.y - a.y);

  // Check the closest header first
  for (const header of nearbyHeaders) {
    const headerLower = header.text.toLowerCase();
    const matchedGroups: ClinicalGroup[] = [];

    for (const [keyword, groups] of Object.entries(HEADER_GROUP_KEYWORDS)) {
      if (headerLower.includes(keyword)) {
        matchedGroups.push(...groups);
      }
    }

    if (matchedGroups.length === 0) continue;

    // Find candidates whose clinical group matches the header
    const headerMatchedCandidates = candidates.filter((c) =>
      matchedGroups.includes(c.clinicalGroup),
    );

    if (headerMatchedCandidates.length === 1) {
      return headerMatchedCandidates[0];
    }
  }

  return null;
}

/**
 * Rule 2: Sibling Biomarker Match
 * If 2+ other biomarkers on the same page belong to one candidate's
 * clinical group, resolve to that candidate.
 */
function resolveBySiblings(input: AmbiguityInput): AmbiguousCandidate | null {
  const { sourceLocation, documentContext, candidates } = input;

  // Count siblings per clinical group on the same page
  const groupCounts = new Map<ClinicalGroup, number>();

  for (const sibling of documentContext.siblingBiomarkers) {
    if (sibling.page !== sourceLocation.page) continue;

    const siblingGroup = BIOMARKER_CLINICAL_GROUPS[sibling.canonicalId];
    if (!siblingGroup) continue;

    groupCounts.set(siblingGroup, (groupCounts.get(siblingGroup) ?? 0) + 1);
  }

  // Find candidates whose group has 2+ siblings
  const resolvedCandidates = candidates.filter((c) => {
    const count = groupCounts.get(c.clinicalGroup) ?? 0;
    return count >= 2;
  });

  if (resolvedCandidates.length === 1) {
    return resolvedCandidates[0];
  }

  return null;
}

/**
 * Rule 3: Reference Range Match
 * Compare extracted reference range against known ranges for each candidate.
 * If the range matches exactly one candidate within 10% tolerance, resolve.
 */
function resolveByReferenceRange(input: AmbiguityInput): AmbiguousCandidate | null {
  const { extractedRange, candidates } = input;

  if (!extractedRange) return null;

  const matchedCandidates: AmbiguousCandidate[] = [];

  for (const candidate of candidates) {
    const knownRanges = KNOWN_REFERENCE_RANGES[candidate.canonicalId];
    if (!knownRanges) continue;

    for (const known of knownRanges) {
      if (rangeMatchesWithTolerance(extractedRange, known, 0.10)) {
        matchedCandidates.push(candidate);
        break; // Only count each candidate once
      }
    }
  }

  if (matchedCandidates.length === 1) {
    return matchedCandidates[0];
  }

  return null;
}

/**
 * Check if an extracted range matches a known range within tolerance.
 * Tolerance is applied as a percentage of the known value.
 */
function rangeMatchesWithTolerance(
  extracted: { low: number; high: number },
  known: KnownRange,
  tolerance: number,
): boolean {
  const lowTolerance = Math.abs(known.low) * tolerance;
  const highTolerance = Math.abs(known.high) * tolerance;

  // Handle zero lower bounds: use absolute tolerance from the high end
  const effectiveLowTolerance = known.low === 0
    ? Math.abs(known.high) * tolerance
    : lowTolerance;

  const lowMatches = Math.abs(extracted.low - known.low) <= effectiveLowTolerance;
  const highMatches = Math.abs(extracted.high - known.high) <= highTolerance;

  return lowMatches && highMatches;
}

/**
 * Rule 4: Unit Match
 * If the extracted unit unambiguously maps to exactly one candidate, resolve.
 */
function resolveByUnit(input: AmbiguityInput): AmbiguousCandidate | null {
  const { extractedUnit, candidates } = input;

  if (!extractedUnit) return null;

  const unitLower = extractedUnit.toLowerCase().trim();

  // Find which candidates are possible for this unit
  let unitCandidateIds: string[] | undefined;

  for (const [unit, ids] of Object.entries(UNIT_TO_CANDIDATES)) {
    if (unit.toLowerCase() === unitLower) {
      unitCandidateIds = ids;
      break;
    }
  }

  if (!unitCandidateIds) return null;

  // Intersect with our ambiguous candidates
  const matchedCandidates = candidates.filter((c) =>
    unitCandidateIds!.includes(c.canonicalId),
  );

  if (matchedCandidates.length === 1) {
    return matchedCandidates[0];
  }

  return null;
}

// ── Main Resolver ──

/**
 * Resolve an ambiguous biomarker name using concrete context rules.
 *
 * Rules are evaluated in order. If exactly one rule produces a result,
 * that result is used. If multiple rules agree on the same candidate,
 * that candidate is used. If rules disagree, defaults to unidentified.
 *
 * Clinical neighbors (estradiol/estriol, T3/T4, ALT/AST) are NEVER
 * resolved by context — they require exact alias match.
 */
export function resolveAmbiguity(input: AmbiguityInput): AmbiguityResult {
  const { rawName, candidates } = input;

  // Safety: if fewer than 2 candidates, this isn't ambiguous
  if (candidates.length < 2) {
    return {
      resolved: false,
      canonicalId: null,
      method: null,
      resolutionNotes: `Not ambiguous: ${candidates.length} candidate(s)`,
      processingFlags: ['unidentified_biomarker'],
    };
  }

  // Clinical neighbor guard: NEVER resolve by context alone
  if (candidatesAreClinicalNeighbors(candidates)) {
    return {
      resolved: false,
      canonicalId: null,
      method: null,
      resolutionNotes: `Clinical neighbors detected among candidates for "${rawName}" (${candidates.map((c) => c.canonicalId).join(', ')}). Context resolution disabled — requires exact alias match.`,
      processingFlags: ['ambiguous', 'clinical_neighbor_conflict'],
    };
  }

  // Evaluate all 4 rules
  const headerResult = resolveByHeader(input);
  const siblingResult = resolveBySiblings(input);
  const rangeResult = resolveByReferenceRange(input);
  const unitResult = resolveByUnit(input);

  const results: { candidate: AmbiguousCandidate; method: ResolutionMethod }[] = [];

  if (headerResult) results.push({ candidate: headerResult, method: 'section_header' });
  if (siblingResult) results.push({ candidate: siblingResult, method: 'sibling_biomarker' });
  if (rangeResult) results.push({ candidate: rangeResult, method: 'reference_range' });
  if (unitResult) results.push({ candidate: unitResult, method: 'unit_match' });

  // No rule resolved → unidentified
  if (results.length === 0) {
    return {
      resolved: false,
      canonicalId: null,
      method: null,
      resolutionNotes: `No resolution rule matched for "${rawName}" with candidates: ${candidates.map((c) => c.canonicalId).join(', ')}`,
      processingFlags: ['ambiguous', 'unidentified_biomarker'],
    };
  }

  // Check if all resolving rules agree
  const resolvedIds = new Set(results.map((r) => r.candidate.canonicalId));

  if (resolvedIds.size === 1) {
    // All rules agree (or only one rule fired)
    const winner = results[0];
    const methods = results.map((r) => r.method).join(', ');

    return {
      resolved: true,
      canonicalId: winner.candidate.canonicalId,
      method: winner.method,
      resolutionNotes: `Ambiguity for "${rawName}" resolved to "${winner.candidate.canonicalId}" via ${methods}`,
      processingFlags: ['context_resolved'],
    };
  }

  // Rules disagree → default to unidentified (never guess)
  const disagreements = results
    .map((r) => `${r.method}→${r.candidate.canonicalId}`)
    .join(', ');

  return {
    resolved: false,
    canonicalId: null,
    method: null,
    resolutionNotes: `Resolution rules disagree for "${rawName}": ${disagreements}. Defaulting to unidentified.`,
    processingFlags: ['ambiguous', 'unidentified_biomarker', 'rules_disagree'],
  };
}

// ── Integration Helper ──

/**
 * Look up the clinical group for a canonical biomarker ID.
 * Returns undefined if not in the group map.
 */
export function getClinicalGroup(canonicalId: string): ClinicalGroup | undefined {
  return BIOMARKER_CLINICAL_GROUPS[canonicalId];
}

/**
 * Build an AmbiguousCandidate from a canonical ID.
 * Returns null if the ID has no known clinical group.
 */
export function buildCandidate(canonicalId: string): AmbiguousCandidate | null {
  const group = BIOMARKER_CLINICAL_GROUPS[canonicalId];
  if (!group) return null;
  return { canonicalId, clinicalGroup: group };
}
